import { FormEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { io, Socket } from 'socket.io-client'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'

export function RoomPage() {
  const { id } = useParams()
  const { token, user } = useAuth()
  const [snapshot, setSnapshot] = useState<any>(null)
  const [presence, setPresence] = useState<any[]>([])
  const [messages, setMessages] = useState<any[]>([])
  const [socket, setSocket] = useState<Socket | null>(null)
  const [text, setText] = useState('')

  useEffect(() => {
    let s: Socket | undefined
    let poll: ReturnType<typeof setInterval> | undefined
    ;(async () => {
      await api(`/api/rooms/${id}/join`, { method: 'POST', token })
      const snap = await api(`/api/rooms/${id}/snapshot`, { token })
      setSnapshot(snap)
      setMessages((snap as any).messages || [])
      setPresence((snap as any).presence || [])

      try {
        s = io(import.meta.env.VITE_API_URL || window.location.origin, {
          auth: { token },
          withCredentials: true,
          transports: ['websocket', 'polling'],
          timeout: 4000,
        })
        s.on('connect_error', () => {
          s?.disconnect()
          setSocket(null)
        })
        s.emit('room:join', { roomId: id }, (ack: any) => {
          if (ack?.snapshot) {
            setSnapshot(ack.snapshot)
            setMessages(ack.snapshot.messages || [])
            setPresence(ack.snapshot.presence || [])
          }
        })
        s.on('presence.updated', (ev) => setPresence(ev.presence || []))
        s.on('chat.message_created', (ev) => {
          setMessages((m) => [...m, ev.message])
        })
        s.on('reaction.created', () => {
          api(`/api/rooms/${id}/snapshot`, { token }).then(setSnapshot)
        })
        setSocket(s)
      } catch {
        setSocket(null)
      }

      poll = setInterval(() => {
        api(`/api/rooms/${id}/snapshot`, { token })
          .then((snap2: any) => {
            setSnapshot(snap2)
            setMessages(snap2.messages || [])
            if (snap2.presence?.length) setPresence(snap2.presence)
          })
          .catch(() => {})
      }, 4000)
    })()
    return () => {
      if (poll) clearInterval(poll)
      s?.emit('room:leave', { roomId: id })
      s?.disconnect()
    }
  }, [id, token])

  async function sendChat(e: FormEvent) {
    e.preventDefault()
    if (!text.trim()) return
    const body = text
    setText('')
    if (socket?.connected) {
      socket.emit('room:chat', { roomId: id, body }, () => {})
    } else {
      const ev = await api<any>(`/api/rooms/${id}/chat`, { method: 'POST', token, body: { body } })
      if (ev?.message) setMessages((m) => [...m, ev.message])
    }
  }

  async function react(emoji: string) {
    if (socket?.connected) {
      socket.emit('room:reaction', { roomId: id, emoji }, () => {})
    } else {
      await api(`/api/rooms/${id}/reactions`, { method: 'POST', token, body: { emoji } })
      const snap2 = await api(`/api/rooms/${id}/snapshot`, { token })
      setSnapshot(snap2)
    }
  }

  async function moderate() {
    await api(`/api/rooms/${id}/moderate`, {
      method: 'POST',
      token,
      body: { action: 'warn', note: 'keep it friendly' },
    })
  }

  return (
    <div className="stack">
      <div className="room-layout">
        <div className="stage-panel">
          <div
            className="stage-bg"
            style={{ backgroundImage: 'url(/assets/mma/watch-room-stage/live-stage.png)' }}
          />
          <div className="stage-content">
            <div className="eyebrow" style={{ color: '#fca5a5', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.1em' }}>
              WATCH ROOM
            </div>
            <h1 style={{ margin: '4px 0 6px', fontSize: 22 }}>{snapshot?.room?.name || 'Fight night room'}</h1>
            <p style={{ margin: 0, color: '#cbd5e1', fontSize: 13 }}>
              Snapshot v{snapshot?.version ?? '—'} ·{' '}
              {socket?.connected ? 'live socket' : 'REST resync (4s)'}
            </p>
            <div className="presence" style={{ marginTop: 12 }} data-testid="presence">
              {presence.map((p) => (
                <span className="chip" key={p.userId} style={{ background: 'rgba(0,0,0,0.45)', color: '#e2e8f0', borderColor: 'rgba(255,255,255,0.15)' }}>
                  <span className="dot" />
                  {p.username}
                  {p.userId === user?.id ? ' (you)' : ''}
                </span>
              ))}
              {!presence.length && <span style={{ color: '#94a3b8' }}>Waiting for friends…</span>}
            </div>
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn secondary" type="button" onClick={() => react('🔥')}>
                🔥
              </button>
              <button className="btn secondary" type="button" onClick={() => react('👏')}>
                👏
              </button>
              <button className="btn secondary" type="button" onClick={moderate}>
                Host warn
              </button>
            </div>
          </div>
        </div>

        <div className="panel">
          <h2>Cage chat</h2>
          <div className="chat" data-testid="chat">
            {messages.map((m) => (
              <div className="msg" key={m.id}>
                <strong>{m.username || m.user_id}</strong>: {m.body}
              </div>
            ))}
            {!messages.length && <div className="muted">No messages yet — say hi.</div>}
          </div>
          <form className="row" style={{ marginTop: 10 }} onSubmit={sendChat}>
            <input
              style={{ flex: 1, padding: '8px 10px', borderRadius: 4, border: '1px solid var(--bevel-lo)' }}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Call the finish…"
              data-testid="chat-input"
            />
            <button className="btn" type="submit">
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
